import {
  useState,
  useMemo,
  useRef,
  useEffect,
  type ReactNode,
  type JSX,
} from 'react';
import { Layout } from '../../components/Layout';
import { Modal } from '../../components/Modal';
import { get, patch, post, del } from '../../api/superAdminClient';
import useSWR from 'swr';
import { Pagination } from '../../components/Pagination';
import { FilterBar, FilterItem } from '../../components/FilterBar';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorDisplay } from '../../components/ErrorDisplay';
import { format } from 'date-fns';
import { zhTW } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { taipeiToUtc } from '@pos/shared/utils/time';

/** 表格欄位設定（順序即顯示順序） */
const COLUMN_OPTIONS: { key: string; label: string }[] = [
  { key: 'name', label: '名稱' },
  { key: 'groupId', label: 'Group ID' },
  { key: 'type', label: '類型' },
  { key: 'ownerName', label: '擁有者' },
  { key: 'activityCountThisMonth', label: '本月活動數' },
  { key: 'subscriptionPlan', label: '方案' },
  { key: 'subscriptionPeriodEnd', label: '付款到期日期' },
  { key: 'subscription', label: '訂閱' },
  { key: 'status', label: '狀態' },
  { key: 'createdAt', label: '建立時間' },
  { key: 'extraPublishQuota', label: '發佈加額' },
  { key: 'action', label: '操作' },
];

/**
 * LITE 群組列表回應類型
 */
interface LiteGroupListResponse {
  success: boolean;
  data: {
    groups: Array<{
      groupId: string;
      name: string;
      type: string | null;
      isActive: boolean;
      ownerName: string | null;
      ownerId: string;
      createdAt: string;
      updatedAt: string;
      subscriptionCancelAtPeriodEnd: boolean;
      subscriptionPlan: string | null;
      subscriptionPeriodEnd: string | null;
      activityUsed: number;
      activityLimit: number;
      extraPublishQuota: number;
    }>;
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * 遮蔽 group_id 顯示（僅顯示前 4 與後 4 字元）
 */
function maskGroupId(groupId: string): string {
  if (groupId.length <= 10) return groupId;
  return `${groupId.slice(0, 4)}...${groupId.slice(-4)}`;
}

type GroupRow = LiteGroupListResponse['data']['groups'][number];

function renderCell(
  key: string,
  g: GroupRow,
  helpers: {
    maskGroupId: (id: string) => string;
    onToggleActive: (x: { groupId: string; isActive: boolean }) => void;
    onEditQuota: (g: GroupRow) => void;
    togglingId: string | null;
  }
): JSX.Element {
  const { maskGroupId, onToggleActive, onEditQuota, togglingId } = helpers;
  const base = 'px-6 py-4 text-sm';
  let cn = base;
  if (key === 'name') cn += ' whitespace-nowrap font-medium text-gray-900';
  else if (key === 'groupId')
    cn += ' whitespace-nowrap text-gray-500 font-mono';
  else if (key === 'status') cn += ' whitespace-nowrap';
  else if (key === 'action') cn += ' whitespace-nowrap font-medium';
  else if (key === 'extraPublishQuota')
    cn += ' whitespace-nowrap text-gray-500 min-w-[8rem] pb-5';
  else cn += ' whitespace-nowrap text-gray-500';

  let content: ReactNode = '';
  switch (key) {
    case 'name':
      content = g.name;
      break;
    case 'groupId':
      content = maskGroupId(g.groupId);
      break;
    case 'type':
      content = g.type || '-';
      break;
    case 'ownerName':
      content = g.ownerName || '-';
      break;
    case 'activityCountThisMonth':
      // 顯示格式：2/8（分子表示已發佈的數量，分母是方案的上限）
      content = `${g.activityUsed}/${g.activityLimit >= 999 ? '∞' : g.activityLimit}`;
      break;
    case 'subscriptionPlan':
      content = g.subscriptionPlan ?? 'free';
      break;
    case 'subscriptionPeriodEnd':
      content = g.subscriptionPeriodEnd
        ? format(new Date(g.subscriptionPeriodEnd), 'yyyy-MM-dd', {
            locale: zhTW,
          })
        : '-';
      break;
    case 'subscription':
      content =
        g.subscriptionPlan && g.subscriptionPeriodEnd
          ? (g.subscriptionCancelAtPeriodEnd ? '不續訂 ' : '訂閱中 ') +
            `${g.subscriptionPlan} 至 ${format(new Date(g.subscriptionPeriodEnd), 'yyyy-MM-dd', { locale: zhTW })}`
          : '無';
      break;
    case 'status':
      content = (
        <span
          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
            g.isActive
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {g.isActive ? '啟用' : '停用'}
        </span>
      );
      break;
    case 'createdAt':
      content = format(new Date(g.createdAt), 'yyyy-MM-dd', { locale: zhTW });
      break;
    case 'extraPublishQuota':
      content = (
        <span className="inline-flex items-center gap-2">
          <span>{g.extraPublishQuota ?? 0}</span>
          <button
            type="button"
            onClick={() => onEditQuota(g)}
            className="text-primary hover:text-primary/80 text-xs font-medium shrink-0 py-0.5 leading-normal"
          >
            設定
          </button>
        </span>
      );
      break;
    case 'action':
      content = (
        <button
          onClick={() => onToggleActive(g)}
          disabled={togglingId === g.groupId}
          className={`text-primary hover:text-primary/80 transition-colors ${togglingId === g.groupId ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {togglingId === g.groupId ? '處理中…' : g.isActive ? '停用' : '啟用'}
        </button>
      );
      break;
    default:
      break;
  }

  return (
    <td key={key} className={cn}>
      {content}
    </td>
  );
}

/**
 * LITE 群組管理頁
 */
const defaultVisibleColumns = (): Record<string, boolean> =>
  Object.fromEntries(COLUMN_OPTIONS.map(c => [c.key, true]));

export function Lite() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState<string>('');
  const [planFilter, setPlanFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [editingQuotaGroup, setEditingQuotaGroup] = useState<{
    groupId: string;
    name: string;
    extraPublishQuota: number;
  } | null>(null);
  const [quotaInputValue, setQuotaInputValue] = useState<string>('0');
  const [savingQuota, setSavingQuota] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    defaultVisibleColumns
  );
  const [columnsPopoverOpen, setColumnsPopoverOpen] = useState(false);
  const columnsPopoverRef = useRef<HTMLDivElement>(null);

  // 公告設定相關狀態
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [announcements, setAnnouncements] = useState<
    Array<{
      id: number;
      content: string;
      startsAt: string;
      endsAt: string;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    }>
  >([]);
  const [editingAnnouncement, setEditingAnnouncement] = useState<{
    id?: number;
    content: string;
    startsAt: string;
    endsAt: string;
    isActive: boolean;
  } | null>(null);
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(false);

  // 退訂分析相關狀態
  const [analyticsModalOpen, setAnalyticsModalOpen] = useState(false);
  const [analytics, setAnalytics] = useState<{
    totalGroups: number;
    planStats: {
      pro: number;
      basic: number;
      free: number;
    };
    cancelledGroups: number;
    reasonStats: Array<{ reason: string; count: number }>;
    missingFeatureCount: number;
    otherReasonCount: number;
  } | null>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  useEffect(() => {
    if (!columnsPopoverOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (columnsPopoverRef.current?.contains(e.target as Node)) return;
      setColumnsPopoverOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [columnsPopoverOpen]);

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('limit', '20');
    if (search) params.set('search', search);
    if (isActiveFilter) params.set('isActive', isActiveFilter);
    if (planFilter) params.set('plan', planFilter);
    if (typeFilter) params.set('type', typeFilter);
    return params.toString();
  }, [page, search, isActiveFilter, planFilter, typeFilter]);

  const { data, error, isLoading, mutate } = useSWR<LiteGroupListResponse>(
    `/api/super-admin/lite/groups?${queryParams}`,
    get as (url: string) => Promise<LiteGroupListResponse>
  );

  const handleSearchChange = (v: string) => {
    setSearch(v);
    setPage(1);
  };

  const handleFilterChange = () => setPage(1);

  const handleClearFilters = () => {
    setSearch('');
    setIsActiveFilter('');
    setPlanFilter('');
    setTypeFilter('');
    setPage(1);
  };

  const handleToggleActive = async (g: {
    groupId: string;
    isActive: boolean;
  }) => {
    if (togglingId) return;
    setTogglingId(g.groupId);
    try {
      await patch(
        `/api/super-admin/lite/groups/${encodeURIComponent(g.groupId)}/is-active`,
        {
          isActive: !g.isActive,
        }
      );
      toast.success(g.isActive ? '已停用' : '已啟用');
      mutate();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '操作失敗';
      toast.error(msg);
    } finally {
      setTogglingId(null);
    }
  };

  const handleEditQuota = (g: GroupRow) => {
    setEditingQuotaGroup({
      groupId: g.groupId,
      name: g.name,
      extraPublishQuota: g.extraPublishQuota ?? 0,
    });
    setQuotaInputValue(String(g.extraPublishQuota ?? 0));
  };

  const handleSaveQuota = async () => {
    if (!editingQuotaGroup) return;
    const parsed = parseInt(quotaInputValue, 10);
    if (Number.isNaN(parsed) || parsed < 0) {
      toast.error('發佈加額請填寫且不可為負數');
      return;
    }
    setSavingQuota(true);
    try {
      await patch<{ success: boolean }>(
        `/api/super-admin/lite/groups/${encodeURIComponent(editingQuotaGroup.groupId)}/extra-publish-quota`,
        { extraPublishQuota: parsed }
      );
      toast.success('發佈加額已更新');
      setEditingQuotaGroup(null);
      mutate();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '更新失敗';
      toast.error(msg);
    } finally {
      setSavingQuota(false);
    }
  };

  const hasActiveFilters = !!(
    search ||
    isActiveFilter ||
    planFilter ||
    typeFilter
  );

  // 載入公告列表
  const loadAnnouncements = async () => {
    setLoadingAnnouncements(true);
    try {
      const res = await get<{ success: boolean; data: typeof announcements }>(
        '/api/super-admin/lite/announcements'
      );
      setAnnouncements(res.data);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '載入失敗';
      toast.error(msg);
    } finally {
      setLoadingAnnouncements(false);
    }
  };

  // 開啟公告設定 modal
  const handleOpenAnnouncementModal = async () => {
    setAnnouncementModalOpen(true);
    await loadAnnouncements();
  };

  // 新增公告
  const handleCreateAnnouncement = () => {
    // 使用當前台北時間
    const now = new Date();
    const taipeiNow = new Date(
      now.toLocaleString('en-US', { timeZone: 'Asia/Taipei' })
    );
    const weekLater = new Date(taipeiNow.getTime() + 7 * 24 * 60 * 60 * 1000);

    setEditingAnnouncement({
      content: '',
      startsAt: format(taipeiNow, "yyyy-MM-dd'T'HH:mm"),
      endsAt: format(weekLater, "yyyy-MM-dd'T'HH:mm"),
      isActive: true,
    });
  };

  // 編輯公告
  const handleEditAnnouncement = (announcement: (typeof announcements)[0]) => {
    // 後端已回傳台北時間，直接使用（格式：YYYY-MM-DDTHH:mm:ss+08:00）
    setEditingAnnouncement({
      id: announcement.id,
      content: announcement.content,
      startsAt: announcement.startsAt.slice(0, 16), // YYYY-MM-DDTHH:mm
      endsAt: announcement.endsAt.slice(0, 16), // YYYY-MM-DDTHH:mm
      isActive: announcement.isActive,
    });
  };

  // 儲存公告
  const handleSaveAnnouncement = async () => {
    if (!editingAnnouncement) return;
    setSavingAnnouncement(true);
    try {
      // 將用戶輸入的台北時間轉換為 UTC
      const startsAtUtc = taipeiToUtc(editingAnnouncement.startsAt);
      const endsAtUtc = taipeiToUtc(editingAnnouncement.endsAt);

      if (editingAnnouncement.id) {
        // 更新
        await patch(
          `/api/super-admin/lite/announcements/${editingAnnouncement.id}`,
          {
            content: editingAnnouncement.content,
            startsAt: startsAtUtc,
            endsAt: endsAtUtc,
            isActive: editingAnnouncement.isActive,
          }
        );
        toast.success('公告已更新');
      } else {
        // 新增
        await post('/api/super-admin/lite/announcements', {
          content: editingAnnouncement.content,
          startsAt: startsAtUtc,
          endsAt: endsAtUtc,
          isActive: editingAnnouncement.isActive,
        });
        toast.success('公告已建立');
      }
      setEditingAnnouncement(null);
      await loadAnnouncements();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '儲存失敗';
      toast.error(msg);
    } finally {
      setSavingAnnouncement(false);
    }
  };

  // 刪除公告
  const handleDeleteAnnouncement = async (id: number) => {
    if (!confirm('確定要刪除此公告嗎？')) return;
    try {
      await del(`/api/super-admin/lite/announcements/${id}`);
      toast.success('公告已刪除');
      await loadAnnouncements();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '刪除失敗';
      toast.error(msg);
    }
  };

  // 載入退訂分析
  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const res = await get<{ success: boolean; data: typeof analytics }>(
        '/api/super-admin/lite/cancellation-analytics'
      );
      setAnalytics(res.data);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '載入失敗';
      toast.error(msg);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // 開啟退訂分析 modal
  const handleOpenAnalyticsModal = async () => {
    setAnalyticsModalOpen(true);
    await loadAnalytics();
  };

  // 退訂理由中文對照
  const reasonLabels: Record<string, string> = {
    cost_over_budget: '超出預算',
    low_value: '價值不符',
    low_usage: '使用頻率低',
    business_ended: '業務結束',
    missing_feature: '缺少功能',
    too_complex: '操作複雜',
    unstable: '系統不穩定',
    slow_support: '客服回應慢',
    switched_competitor: '改用競爭對手',
    no_longer_needed: '不再需要',
    taking_break: '暫時休息',
    other: '其他',
  };

  return (
    <Layout title="LITE 群組">
      <Modal
        isOpen={!!editingQuotaGroup}
        onClose={() => setEditingQuotaGroup(null)}
        title={`設定發佈加額 － ${editingQuotaGroup?.name ?? ''}`}
        footer={
          <>
            <button
              type="button"
              onClick={() => setEditingQuotaGroup(null)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              取消
            </button>
            <button
              type="button"
              onClick={handleSaveQuota}
              disabled={savingQuota}
              className="px-4 py-2 text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingQuota ? '儲存中…' : '儲存'}
            </button>
          </>
        }
      >
        <div>
          <label
            htmlFor="quota-input"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            發佈加額（加在方案月發佈上限）
          </label>
          <input
            id="quota-input"
            type="number"
            min={0}
            placeholder="0"
            value={quotaInputValue}
            onChange={e => setQuotaInputValue(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </Modal>

      <div className="space-y-4">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleOpenAnnouncementModal}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            公告設定
          </button>
          <button
            type="button"
            onClick={handleOpenAnalyticsModal}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            退訂分析
          </button>
        </div>
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow p-4">
            <input
              type="text"
              placeholder="搜尋群組名稱或 Group ID…"
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <FilterBar
            onClear={handleClearFilters}
            showClearButton={hasActiveFilters}
          >
            <FilterItem label="狀態">
              <select
                value={isActiveFilter}
                onChange={e => {
                  setIsActiveFilter(e.target.value);
                  handleFilterChange();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">全部</option>
                <option value="true">啟用</option>
                <option value="false">停用</option>
              </select>
            </FilterItem>
            <FilterItem label="方案">
              <select
                value={planFilter}
                onChange={e => {
                  setPlanFilter(e.target.value);
                  handleFilterChange();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">全部</option>
                <option value="free">免費</option>
                <option value="basic">一般</option>
                <option value="pro">專業</option>
              </select>
            </FilterItem>
            <FilterItem label="類型">
              <select
                value={typeFilter}
                onChange={e => {
                  setTypeFilter(e.target.value);
                  handleFilterChange();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">全部</option>
                <option value="商品">商品</option>
                <option value="席次">席次</option>
                <option value="席次(羽球)">席次(羽球)</option>
              </select>
            </FilterItem>
            <FilterItem label="自訂欄位">
              <div ref={columnsPopoverRef} className="relative">
                <button
                  type="button"
                  onClick={() => setColumnsPopoverOpen(o => !o)}
                  className="w-full px-3 py-2 text-left text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  勾選
                </button>
                {columnsPopoverOpen && (
                  <div className="absolute left-0 top-full mt-1 z-20 w-56 py-2 bg-white border border-gray-200 rounded-lg shadow-lg">
                    <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-100">
                      顯示欄位
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {COLUMN_OPTIONS.map(c => (
                        <label
                          key={c.key}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={!!visibleColumns[c.key]}
                            onChange={() =>
                              setVisibleColumns(prev => ({
                                ...prev,
                                [c.key]: !prev[c.key],
                              }))
                            }
                            className="rounded border-gray-300 text-primary focus:ring-primary"
                          />
                          <span className="text-sm text-gray-700">
                            {c.label}
                          </span>
                        </label>
                      ))}
                    </div>
                    <div className="px-3 py-2 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={() =>
                          setVisibleColumns(defaultVisibleColumns())
                        }
                        className="text-xs text-primary hover:text-primary/80"
                      >
                        全部勾選
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </FilterItem>
          </FilterBar>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="p-8">
              <ErrorDisplay error={error} onRetry={() => mutate()} />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {COLUMN_OPTIONS.filter(c => visibleColumns[c.key]).map(
                        c => (
                          <th
                            key={c.key}
                            className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${c.key === 'extraPublishQuota' ? 'min-w-[8rem]' : ''}`}
                          >
                            {c.label}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {!data?.data?.groups || data.data.groups.length === 0 ? (
                      <tr>
                        <td
                          colSpan={Math.max(
                            1,
                            COLUMN_OPTIONS.filter(c => visibleColumns[c.key])
                              .length
                          )}
                          className="px-6 py-4 text-center text-gray-500"
                        >
                          尚無資料
                        </td>
                      </tr>
                    ) : (
                      data.data.groups.map(g => (
                        <tr key={g.groupId} className="hover:bg-gray-50">
                          {COLUMN_OPTIONS.filter(
                            c => visibleColumns[c.key]
                          ).map(c =>
                            renderCell(c.key, g, {
                              maskGroupId,
                              onToggleActive: handleToggleActive,
                              onEditQuota: handleEditQuota,
                              togglingId,
                            })
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {data?.data && data.data.totalPages > 1 && (
                <div className="px-6 py-4 border-t">
                  <Pagination
                    currentPage={data.data.page}
                    totalPages={data.data.totalPages}
                    total={data.data.total}
                    limit={data.data.limit}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* 公告設定 Modal */}
      <Modal
        isOpen={announcementModalOpen}
        onClose={() => {
          setAnnouncementModalOpen(false);
          setEditingAnnouncement(null);
        }}
        title="公告設定"
        footer={
          <>
            {editingAnnouncement ? (
              <>
                <button
                  type="button"
                  onClick={() => setEditingAnnouncement(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={handleSaveAnnouncement}
                  disabled={
                    savingAnnouncement || !editingAnnouncement.content.trim()
                  }
                  className="px-4 py-2 text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingAnnouncement ? '儲存中…' : '儲存'}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setAnnouncementModalOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                關閉
              </button>
            )}
          </>
        }
      >
        {editingAnnouncement ? (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="announcement-content"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                公告內容
              </label>
              <textarea
                id="announcement-content"
                value={editingAnnouncement.content}
                onChange={e =>
                  setEditingAnnouncement({
                    ...editingAnnouncement,
                    content: e.target.value,
                  })
                }
                rows={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="輸入公告內容..."
              />
            </div>
            <div>
              <label
                htmlFor="announcement-starts-at"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                開始時間
              </label>
              <input
                id="announcement-starts-at"
                type="datetime-local"
                value={editingAnnouncement.startsAt}
                onChange={e =>
                  setEditingAnnouncement({
                    ...editingAnnouncement,
                    startsAt: e.target.value,
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label
                htmlFor="announcement-ends-at"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                結束時間
              </label>
              <input
                id="announcement-ends-at"
                type="datetime-local"
                value={editingAnnouncement.endsAt}
                onChange={e =>
                  setEditingAnnouncement({
                    ...editingAnnouncement,
                    endsAt: e.target.value,
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingAnnouncement.isActive}
                  onChange={e =>
                    setEditingAnnouncement({
                      ...editingAnnouncement,
                      isActive: e.target.checked,
                    })
                  }
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700">啟用</span>
              </label>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleCreateAnnouncement}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                新增公告
              </button>
            </div>
            {loadingAnnouncements ? (
              <div className="text-center py-8">
                <LoadingSpinner />
              </div>
            ) : announcements.length === 0 ? (
              <p className="text-center text-gray-500 py-8">尚無公告</p>
            ) : (
              <div className="space-y-3">
                {announcements.map(announcement => (
                  <div
                    key={announcement.id}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <p className="text-sm text-gray-600 mb-1">
                          {format(
                            new Date(announcement.startsAt),
                            'yyyy-MM-dd HH:mm',
                            { locale: zhTW }
                          )}{' '}
                          至{' '}
                          {format(
                            new Date(announcement.endsAt),
                            'yyyy-MM-dd HH:mm',
                            { locale: zhTW }
                          )}
                        </p>
                        <p className="text-gray-900 whitespace-pre-wrap">
                          {announcement.content}
                        </p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            announcement.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {announcement.isActive ? '啟用' : '停用'}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => handleEditAnnouncement(announcement)}
                        className="px-3 py-1 text-sm text-primary hover:text-primary/80"
                      >
                        編輯
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleDeleteAnnouncement(announcement.id)
                        }
                        className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                      >
                        刪除
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 退訂分析 Modal */}
      <Modal
        isOpen={analyticsModalOpen}
        onClose={() => setAnalyticsModalOpen(false)}
        title="退訂分析"
        footer={
          <button
            type="button"
            onClick={() => setAnalyticsModalOpen(false)}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            關閉
          </button>
        }
      >
        {loadingAnalytics ? (
          <div className="text-center py-8">
            <LoadingSpinner />
          </div>
        ) : analytics ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">總社群</p>
                <p className="text-2xl font-bold text-blue-600">
                  {analytics.totalGroups}
                </p>
              </div>
              <div className="bg-red-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">退訂</p>
                <p className="text-2xl font-bold text-red-600">
                  {analytics.cancelledGroups}
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                方案統計
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">專業</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {analytics.planStats.pro}
                  </p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">一般</p>
                  <p className="text-2xl font-bold text-indigo-600">
                    {analytics.planStats.basic}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">免費</p>
                  <p className="text-2xl font-bold text-gray-600">
                    {analytics.planStats.free}
                  </p>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                退訂理由統計
              </h3>
              <div className="space-y-2">
                {analytics.reasonStats
                  .filter(stat => stat.count > 0)
                  .map(stat => (
                    <div
                      key={stat.reason}
                      className="flex items-center justify-between"
                    >
                      <span className="text-sm text-gray-700">
                        {reasonLabels[stat.reason] || stat.reason}
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        {stat.count}
                      </span>
                    </div>
                  ))}
                {analytics.missingFeatureCount > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      缺少功能（詳細說明）
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {analytics.missingFeatureCount}
                    </span>
                  </div>
                )}
                {analytics.otherReasonCount > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      其他原因（詳細說明）
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {analytics.otherReasonCount}
                    </span>
                  </div>
                )}
                {analytics.reasonStats.every(stat => stat.count === 0) &&
                  analytics.missingFeatureCount === 0 &&
                  analytics.otherReasonCount === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">
                      尚無退訂資料
                    </p>
                  )}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-gray-500 py-8">載入失敗</p>
        )}
      </Modal>
    </Layout>
  );
}
